import {
  IconButton,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import {
  AreaCustom,
  ChoroplethMap,
  CircleDash,
  Connect,
  Information,
  PenFountain,
  SoftwareResourceReference,
  Undo,
} from '@carbon/react/icons';
import { useRef } from 'react';
import type { CarbonIconType } from '@carbon/icons-react';
import { useEscapeClosesPopover } from './hooks';
import type { EditorSession, EditorTool } from './types';
import { DRAWING_TOOL_HELP, EDITOR_TOOLS } from './utils';
import styles from './area-geometry-editor.module.scss';

const TOOL_ICONS: Record<EditorTool, CarbonIconType> = {
  circle: CircleDash,
  polygon: SoftwareResourceReference,
  move: AreaCustom,
  trace: PenFountain,
};

const HELP_ICONS: Record<
  (typeof DRAWING_TOOL_HELP)[number]['id'],
  CarbonIconType
> = { ...TOOL_ICONS, snap: Connect, landGrid: ChoroplethMap, undo: Undo };

/**
 * How each tool works, behind the info button that starts the toolbar.
 *
 * @return {JSX.Element} The toggletip.
 */
function DrawingToolsHelp(): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  useEscapeClosesPopover(ref);
  return (
    <span ref={ref} className={styles.toolHelpHost}>
      <Toggletip align="bottom-right" className={styles.toolHelp}>
        <ToggletipButton label="How to use the drawing tools">
          <Information />
        </ToggletipButton>
        <ToggletipContent>
          <div className={styles.toolHelpBody}>
            <p className={styles.toolHelpTitle}>Drawing tools</p>
            <dl className={styles.toolHelpList}>
              {DRAWING_TOOL_HELP.map(({ id, name, how }) => {
                const Icon = HELP_ICONS[id];
                return (
                  <div key={id}>
                    <dt>
                      <Icon aria-hidden="true" />
                      {name}
                    </dt>
                    <dd>{how}</dd>
                  </div>
                );
              })}
            </dl>
            <p className={styles.toolHelpFoot}>
              Esc cancels a circle, polygon, or trace in progress.
            </p>
          </div>
        </ToggletipContent>
      </Toggletip>
    </span>
  );
}

/**
 * The Map panel's header: its title, then the drawing tools' help, the
 * construction tools, Snap and Undo, each strip ruled off as the mock draws them.
 * The active tool is primary. Picking a tool from Area from Survey switches the
 * method to User-drawn.
 *
 * @param {{ session: EditorSession }} props - The editing session.
 * @return {JSX.Element} The header row.
 */
export function EditorToolbar({
  session,
}: {
  session: EditorSession;
}): JSX.Element {
  const { tool, setTool, snap, setSnap, method, edit, draft } = session;
  const { landGridOn, setLandGridOn } = session;
  return (
    <div className={styles.mapHeader}>
      <span className={styles.mapTitle}>Map</span>
      <div className={styles.mapStatus}>
        <div
          className={styles.mapTools}
          role="toolbar"
          aria-label="Construction tools"
        >
          <DrawingToolsHelp />
          {EDITOR_TOOLS.map(({ id, label }) => {
            const Icon = TOOL_ICONS[id];
            return (
              <IconButton
                key={id}
                label={label}
                align="bottom"
                kind={tool === id ? 'primary' : 'ghost'}
                size="sm"
                // Trace follows the county and state lines as well as any
                // shown area, so there is always a boundary to pick up.
                disabled={draft.saving}
                onClick={() => setTool(id)}
              >
                <Icon />
              </IconButton>
            );
          })}
        </div>
        <div
          className={styles.mapTools}
          role="toolbar"
          aria-label="Snapping and tracing"
        >
          <IconButton
            label={snap ? 'Snap: on' : 'Snap: off'}
            align="bottom"
            kind={snap ? 'primary' : 'ghost'}
            size="sm"
            disabled={method === 'survey-description' || draft.saving}
            onClick={() => setSnap((on) => !on)}
          >
            <Connect />
          </IconButton>
          {/* The land grid as a snap and trace target, whatever the tool or method:
              off to start with, since county and state lines are what most traces
              follow. The base map draws the grid either way. */}
          <IconButton
            label={landGridOn ? 'Land grid: on' : 'Land grid: off'}
            align="bottom"
            kind={landGridOn ? 'primary' : 'ghost'}
            size="sm"
            disabled={draft.saving}
            onClick={() => setLandGridOn((on) => !on)}
          >
            <ChoroplethMap />
          </IconButton>
        </div>
        <IconButton
          label="Undo edit"
          align="bottom"
          kind="ghost"
          size="sm"
          disabled={!edit.canUndo || draft.saving}
          onClick={edit.undo}
        >
          <Undo />
        </IconButton>
      </div>
    </div>
  );
}
